import React from 'react';
import { View, Text, Pressable, StyleSheet, ViewStyle } from 'react-native';
import { useTheme, SPACING, TYPOGRAPHY } from '../../theme';
import { CURSOR_POINTER } from '../../utils/webStyles';

export interface TextTab {
  key: string;
  label: string;
  /** Override when the visible word isn't enough context on its own
   *  (e.g. "Progress" as an observation category). */
  accessibilityLabel?: string;
}

interface TextTabsProps {
  tabs: TextTab[];
  activeKey: string;
  onChange: (key: string) => void;
  /**
   * 'tabs' (default) — filtering a list that's already on screen.
   * 'radiogroup' — choosing one value for the thing being edited (the
   * create screen's observation type). Same look, honest semantics.
   */
  mode?: 'tabs' | 'radiogroup';
  style?: ViewStyle;
  testID?: string;
}

/**
 * Underlined text tabs — the Plaud-grammar replacement for filter chip rows
 * and for boxed-card option pairs. Inactive tabs are quiet gray; the active
 * tab is ink with a 2px underline sitting on the row's hairline rule.
 */
export function TextTabs({
  tabs,
  activeKey,
  onChange,
  mode = 'tabs',
  style,
  testID,
}: TextTabsProps) {
  const { colors } = useTheme();
  const isRadio = mode === 'radiogroup';

  return (
    <View
      role={isRadio ? 'radiogroup' : 'tablist'}
      testID={testID}
      style={[styles.row, { borderBottomColor: colors.border }, style]}>
      {tabs.map((tab) => {
        const active = tab.key === activeKey;
        return (
          <Pressable
            key={tab.key}
            // RN's aria-* props, not accessibilityState: react-native-web
            // 0.21 doesn't emit DOM aria-* from accessibilityState.
            role={isRadio ? 'radio' : 'tab'}
            aria-selected={isRadio ? undefined : active}
            aria-checked={isRadio ? active : undefined}
            accessibilityLabel={tab.accessibilityLabel ?? tab.label}
            testID={testID ? `${testID}-${tab.key}` : undefined}
            onPress={() => onChange(tab.key)}
            style={[styles.tab, CURSOR_POINTER, active && { borderBottomColor: colors.text }]}>
            <Text
              style={[
                styles.label,
                // textSecondary, not textTertiary: inactive tabs are still
                // interactive text and must hold AA contrast (audit:a11y).
                { color: active ? colors.text : colors.textSecondary },
                active && styles.labelActive,
              ]}>
              {tab.label}
            </Text>
          </Pressable>
        );
      })}
    </View>
  );
}

const styles = StyleSheet.create({
  row: {
    flexDirection: 'row',
    gap: SPACING.lg,
    borderBottomWidth: 1,
  },
  tab: {
    paddingBottom: SPACING.sm,
    // The 2px active underline overlaps the row hairline
    marginBottom: -1,
    borderBottomWidth: 2,
    borderBottomColor: 'transparent',
  },
  label: {
    ...TYPOGRAPHY.label,
  },
  labelActive: {
    fontFamily: TYPOGRAPHY.h4.fontFamily, // semibold when active
  },
});
